import { SessionData, DayStats } from '../hooks/useStatistics';

export interface StatisticsBackupData {
  sessions: SessionData[];
  generatedAt: string;
  version: string;
}

class StatisticsService {
  private readonly SESSIONS_KEY = 'deletoSessions';
  private readonly BACKUP_KEY = 'deletoStatsBackup';
  private readonly VERSION = '1.0.0';

  /**
   * Get all saved sessions from localStorage
   */
  getSessions(): SessionData[] {
    try {
      const sessionsData = localStorage.getItem(this.SESSIONS_KEY);
      return sessionsData ? JSON.parse(sessionsData) : [];
    } catch (error) {
      console.error('Error loading sessions:', error);
      return [];
    }
  }

  /**
   * Save sessions to localStorage
   */
  saveSessions(sessions: SessionData[]): boolean {
    try {
      localStorage.setItem(this.SESSIONS_KEY, JSON.stringify(sessions));
      return true;
    } catch (error) {
      console.error('Error saving sessions:', error);
      return false;
    }
  }

  /**
   * Add a new session with additional statistics data
   */
  addSession(session: SessionData): boolean {
    try {
      const sessions = this.getSessions();
      
      // Add additional metadata for statistics
      const enhancedSession: SessionData = {
        ...session,
        sessionDuration: session.sessionDuration || 0,
        deletedChars: session.deletedChars || 0,
        deletedWords: session.deletedWords || 0,
      };

      sessions.unshift(enhancedSession);
      return this.saveSessions(sessions);
    } catch (error) {
      console.error('Error adding session:', error);
      return false;
    }
  }

  /**
   * Delete a session by ID
   */
  deleteSession(sessionId: number): boolean {
    try {
      const sessions = this.getSessions();
      const filteredSessions = sessions.filter(session => session.id !== sessionId);
      return this.saveSessions(filteredSessions);
    } catch (error) {
      console.error('Error deleting session:', error);
      return false;
    }
  }

  /**
   * Get a session by ID
   */
  getSessionById(sessionId: number): SessionData | null {
    const sessions = this.getSessions();
    return sessions.find(session => session.id === sessionId) || null;
  }

  /**
   * Calculate daily statistics from sessions
   */
  getDailyStats(): DayStats[] {
    const sessions = this.getSessions();
    const dailyStatsMap = new Map<string, DayStats>();

    sessions.forEach(session => {
      const date = new Date(session.date).toISOString().split('T')[0];
      const existing = dailyStatsMap.get(date);

      if (existing) {
        existing.sessions += 1;
        existing.totalWords += session.wordCount || 0;
        existing.totalChars += session.charCount || 0;
        existing.totalTime += session.sessionDuration || 0;
        existing.deletedChars += session.deletedChars || 0;
        existing.deletedWords += session.deletedWords || 0;
      } else {
        dailyStatsMap.set(date, {
          date,
          sessions: 1,
          totalWords: session.wordCount || 0,
          totalChars: session.charCount || 0,
          totalTime: session.sessionDuration || 0,
          deletedChars: session.deletedChars || 0,
          deletedWords: session.deletedWords || 0,
          numTimesDeleto: session.numTimesDeleto || 0,
        });
      }
    });

    return Array.from(dailyStatsMap.values()).sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }

  /**
   * Create a backup of all statistics data
   */
  createBackup(): StatisticsBackupData {
    const sessions = this.getSessions();
    return {
      sessions,
      generatedAt: new Date().toISOString(),
      version: this.VERSION
    };
  }

  /**
   * Export statistics as downloadable JSON file
   */
  exportStatistics(): void {
    try {
      const backup = this.createBackup();
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.href = url;
      link.download = `deleto-statistics-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting statistics:', error);
      throw new Error('Failed to export statistics');
    }
  }

  /**
   * Import statistics from backup file
   */
  async importStatistics(file: File, mergeMode: 'replace' | 'merge' = 'merge'): Promise<boolean> {
    try {
      const text = await file.text();
      const backup: StatisticsBackupData = JSON.parse(text);
      
      // Validate backup format
      if (!backup.sessions || !Array.isArray(backup.sessions)) {
        throw new Error('Invalid backup format');
      }

      if (mergeMode === 'replace') {
        return this.saveSessions(backup.sessions);
      } else {
        // Merge mode: combine with existing sessions
        const existingSessions = this.getSessions();
        const existingIds = new Set(existingSessions.map(s => s.id));
        const newSessions = backup.sessions.filter(s => !existingIds.has(s.id));
        const mergedSessions = [...existingSessions, ...newSessions];
        
        // Sort by date (newest first)
        mergedSessions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        
        return this.saveSessions(mergedSessions);
      }
    } catch (error) {
      console.error('Error importing statistics:', error);
      return false;
    }
  }

  /**
   * Clear all statistics data
   */
  clearAllData(): boolean {
    try {
      localStorage.removeItem(this.SESSIONS_KEY);
      localStorage.removeItem(this.BACKUP_KEY);
      return true;
    } catch (error) {
      console.error('Error clearing data:', error);
      return false;
    }
  }

  /**
   * Get storage usage information
   */
  getStorageInfo(): { used: string; available: string; percentage: number } {
    try {
      const sessions = this.getSessions();
      const dataSize = JSON.stringify(sessions).length;
      const maxSize = 5 * 1024 * 1024; // Assume 5MB limit for localStorage
      
      return {
        used: this.formatBytes(dataSize),
        available: this.formatBytes(maxSize - dataSize),
        percentage: Math.round((dataSize / maxSize) * 100)
      };
    } catch (error) {
      console.error('Error calculating storage info:', error);
      return { used: 'Unknown', available: 'Unknown', percentage: 0 };
    }
  }

  /**
   * Format bytes into human readable string
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

// Export singleton instance
export const statisticsService = new StatisticsService();
export default statisticsService; 